import { useState } from 'react'
import InspectionModal from '../Sections/mechanical/InspectionModal'
import { useGetServicesQuery, useCreateBookingMutation } from '../store/api/services'

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7']

function BookingModal({ onClose }) {
  const { data: services = [], isLoading } = useGetServicesQuery()
  const [createBooking, { isLoading: submitting }] = useCreateBookingMutation()

  const [step, setStep] = useState(1)
  const [selected, setSelected] = useState(null)
  const [done, setDone] = useState(false)
  const [form, setForm] = useState({ customer_name: '', vehicle: '', scheduled_at: '', status: 'pending' })

  const handleSelect = (svc) => { setSelected(svc); setTimeout(() => setStep(2), 250) }
  const handleInput = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }))

  const handleSubmit = async () => {
    try {
      await createBooking({ ...form, service: selected.id, amount: selected.price }).unwrap()
      setDone(true)
    } catch { }
  }

  const canSubmit = form.customer_name && form.vehicle && form.scheduled_at && !submitting

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <style>{`
        @keyframes modalIn {
          from { opacity:0; transform:scale(0.94) translateY(24px); }
          to   { opacity:1; transform:scale(1)    translateY(0);    }
        }
        @keyframes fadeUp {
          from { opacity:0; transform:translateY(14px); }
          to   { opacity:1; transform:translateY(0);    }
        }
        .svc-tile { cursor:pointer; transition:all 0.2s; }
        .svc-tile:hover { transform:translateY(-3px); }
        .bk-input { transition:border-color 0.2s; }
        .bk-input:focus { border-color:#ef4444 !important; outline:none; }
        .confirm-btn:not(:disabled):hover { opacity:0.88; transform:translateY(-1px); }
        .confirm-btn { transition:all 0.2s; }
      `}</style>

      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(160deg,#111118 0%,#0c0c12 100%)',
          border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 20,
          width: '100%',
          maxWidth: step === 1 ? 780 : 520,
          maxHeight: '92vh',
          boxShadow: '0 32px 100px rgba(0,0,0,0.8)',
          animation: 'modalIn 0.32s cubic-bezier(0.34,1.4,0.64,1)',
          transition: 'max-width 0.35s ease',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '22px 28px 18px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#fff', letterSpacing: '0.01em' }}>
              {done ? 'Booking Confirmed 🎉' : step === 1 ? 'Choose a Service' : 'Your Details'}
            </div>
            {!done && (
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', marginTop: 3 }}>
                {step === 1 ? 'Select the service you need' : `${selected?.name} — Step 2 of 2`}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              width: 34, height: 34, borderRadius: 9,
              border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.05)',
              color: 'rgba(255,255,255,0.55)', cursor: 'pointer',
              fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Progress bar */}
        {!done && (
          <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', flexShrink: 0 }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg,#ef4444,#f97316)',
              width: step === 1 ? '50%' : '100%',
              transition: 'width 0.4s ease',
            }} />
          </div>
        )}

        {/* Body */}
        <div style={{ padding: '24px 28px 28px', overflowY: 'auto', flex: 1 }}>

          {/* SUCCESS */}
          {done && (
            <div style={{ textAlign: 'center', padding: '32px 0', animation: 'fadeUp 0.4s ease' }}>
              <div style={{ fontSize: 56, marginBottom: 14 }}>✅</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
                Appointment Booked!
              </div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}>
                Your <strong style={{ color: '#ef4444' }}>{selected?.name}</strong> appointment<br />
                has been received. We'll confirm shortly.
              </div>
              <button onClick={onClose} style={{
                marginTop: 24, padding: '11px 32px', borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg,#ef4444,#dc2626)',
                color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}>Done</button>
            </div>
          )}

          {/* STEP 1 — Service Grid */}
          {!done && step === 1 && (
            <div style={{ animation: 'fadeUp 0.3s ease' }}>
              {isLoading ? (
                <div style={{ color: 'rgba(255,255,255,0.35)', textAlign: 'center', padding: '40px 0' }}>
                  Loading services...
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                  gap: 14,
                }}>
                  {services.map((svc, i) => {
                    const color = COLORS[i % COLORS.length]
                    return (
                      <div
                        key={svc.id}
                        className="svc-tile"
                        onClick={() => handleSelect(svc)}
                        style={{
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          borderRadius: 14,
                          padding: '20px 18px',
                          position: 'relative', overflow: 'hidden',
                        }}
                      >
                        <div style={{
                          position: 'absolute', top: 0, left: 0, right: 0,
                          height: 3, background: color, borderRadius: '14px 14px 0 0',
                        }} />
                        <div style={{
                          width: 42, height: 42, borderRadius: 12,
                          background: `${color}18`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          marginBottom: 14,
                        }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="3" />
                            <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
                          </svg>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#fff', marginBottom: 6, lineHeight: 1.3 }}>
                          {svc.name}
                        </div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                          </svg>
                          {svc.duration_hours} hrs
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 18, fontWeight: 800, color, fontFamily: 'monospace' }}>
                            ₹{parseFloat(svc.price).toLocaleString('en-IN')}
                          </span>
                          <span style={{
                            fontSize: 11, fontWeight: 600, color,
                            background: `${color}18`, border: `1px solid ${color}40`,
                            borderRadius: 6, padding: '3px 10px',
                          }}>Select →</span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* STEP 2 — Details Form */}
          {!done && step === 2 && (
            <div style={{ animation: 'fadeUp 0.3s ease' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                borderRadius: 12, padding: '12px 16px', marginBottom: 22,
              }}>
                <div>
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', marginBottom: 3 }}>
                    SELECTED SERVICE
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{selected?.name}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                    ⏱ {selected?.duration_hours} hrs
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#ef4444', fontFamily: 'monospace' }}>
                    ₹{parseFloat(selected?.price || 0).toLocaleString('en-IN')}
                  </div>
                  <button
                    onClick={() => setStep(1)}
                    style={{
                      fontSize: 11, color: 'rgba(255,255,255,0.4)', background: 'transparent',
                      border: 'none', cursor: 'pointer', textDecoration: 'underline', marginTop: 4,
                    }}
                  >Change</button>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  { label: 'Your Name', name: 'customer_name', type: 'text', placeholder: 'e.g. Arjun Menon' },
                  { label: 'Vehicle', name: 'vehicle', type: 'text', placeholder: 'e.g. Rolls Royce Silver Shadow' },
                  { label: 'Preferred Date & Time', name: 'scheduled_at', type: 'datetime-local', placeholder: '' },
                ].map(({ label, name, type, placeholder }) => (
                  <div key={name}>
                    <div style={{
                      fontSize: 11, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase',
                      letterSpacing: '0.12em', marginBottom: 7,
                    }}>{label}</div>
                    <input
                      className="bk-input"
                      name={name} type={type} placeholder={placeholder}
                      value={form[name]} onChange={handleInput} required
                      style={{
                        width: '100%', background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9,
                        padding: '11px 14px', color: '#fff', fontSize: 14,
                        boxSizing: 'border-box', colorScheme: 'dark',
                      }}
                    />
                  </div>
                ))}
              </div>

              <button
                className="confirm-btn"
                onClick={handleSubmit}
                disabled={!canSubmit}
                style={{
                  width: '100%', marginTop: 22, padding: '14px 0', borderRadius: 11,
                  border: 'none', fontWeight: 700, fontSize: 15, cursor: canSubmit ? 'pointer' : 'not-allowed',
                  background: 'linear-gradient(135deg,#ef4444,#dc2626)',
                  color: '#fff', letterSpacing: '0.03em',
                  opacity: canSubmit ? 1 : 0.45,
                }}
              >
                {submitting ? 'Booking...' : 'Confirm Appointment'}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

// ── Banner ────────────────────────────────────────────────────────────────────
const Banner = () => {
  const [showBooking, setShowBooking] = useState(false)

  return (
    <section className="relative w-full min-h-[100dvh] md:h-screen text-white overflow-hidden">

      {showBooking && <InspectionModal title="Book An Appointment" onClose={() => setShowBooking(false)} />}

      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video className="w-full h-full object-cover" autoPlay loop muted playsInline>
          <source src="https://autobe.ajpgroup.com/api/video/auto.mp4/" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/10 to-black/85" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-[100dvh] md:h-screen flex flex-col justify-between items-start px-4 sm:px-6 md:px-8 lg:px-12 xl:px-20 pt-24 sm:pt-28 md:pt-20 pb-10 sm:pb-24 md:pb-10">
        {/* Top: heading */}
        <div className="max-w-3xl w-full">

          <h1 className="text-2xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 sm:mb-5 md:mb-6 leading-tight">
            European & American Car
            <span className="block text-red-500 mt-1 sm:mt-2">Restorations & Maintenance</span>
          </h1>
        </div>

        {/* Bottom: paragraph + CTA */}
        <div className="max-w-3xl w-full">
          <p className="text-sm sm:text-base md:text-lg mb-5 sm:mb-7 md:mb-8 text-gray-200 leading-relaxed max-w-2xl">
            Established in 2010 catering to high end luxury and classic car projects, upgrades and maintenance. Based in Pune & Bangalore.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 ">
            <button
              onClick={() => setShowBooking(true)}
              className="group bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 sm:px-5 sm:py-2.5 font-semibold text-sm sm:text-base hover:from-red-700 hover:to-red-800 transition-all duration-300 shadow-md hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-1.5"
            >
              <span> Book An Appointment</span>
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>

        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="hidden sm:block absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-10">
        <div className="flex flex-col items-center">
          <span className="text-xs sm:text-sm mb-1 sm:mb-2 text-gray-300">Scroll to explore</span>
          <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>

    </section>
  )
}

export default Banner